import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Sparkles } from "lucide-react"; // Added icons

const About = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const features = [
    {
      title: "National Hackathon",
      description:
        "Compete in a high-intensity national-level hackathon where ideas turn into impactful solutions.",
      icon: "◇",
      color: "blue" as const,
    },
    {
      title: "25+ Tech Events",
      description:
        "Participate in a wide range of technical events covering coding, AI, cybersecurity, and innovation.",
      icon: "○",
      color: "red" as const,
    },
    {
      title: "Workshops & Seminars",
      description:
        "Learn from industry experts through hands-on workshops and insightful technical seminars.",
      icon: "△",
      color: "blue" as const,
    },
    {
      title: "₹1 Lakh+ Prize Pool",
      description:
        "Win exciting rewards including a ₹1 lakh+ prize pool and an exclusive 24K gold coin.",
      icon: "□",
      color: "red" as const,
    },
  ];

  const stats = [
    { value: "2000+", label: "Attendees" },
    { value: "₹1 Lakh", label: "Prize Pool" },
    { value: "25+", label: "Events" },
    { value: "2", label: "Days" },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated Grid Background */}
      <div className="fixed inset-0 grid-tron opacity-50" />

      {/* Floating Geometric Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-32 h-32 neon-circle animate-glow-pulse opacity-30" />
        <div className="absolute bottom-40 left-10 w-20 h-20 neon-circle-red animate-glow-pulse-red opacity-30" />
        <div className="absolute top-1/2 right-10 w-16 h-16 neon-circle animate-glow-pulse opacity-20" />
        <div className="absolute top-1/3 left-1/4 w-24 h-24 neon-circle-red animate-glow-pulse-red opacity-20" />
      </div>

      {/* Scan Line Effect */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-full h-px bg-primary/30 animate-[scan-line_8s_linear_infinite]" />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 py-20">
          <div className="container max-w-6xl mx-auto">
            <div
              className={`text-center transition-all duration-1000 ${
                isLoaded
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              {/* Decorative Lines */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="neon-line w-20 md:w-40" />
                <div className="w-3 h-3 rotate-45 border border-primary glow-blue" />
                <div className="neon-line w-20 md:w-40" />
              </div>

              {/* Main Title */}
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black mb-6 tracking-wider">
                <span className="text-primary text-glow-blue animate-flicker">
                  ABOUT
                </span>
                <br />
                <span className="text-secondary text-glow-red">SYMPOSIUM</span>
              </h1>

              {/* Subtitle */}
              <p className="text-foreground/80 text-xl md:text-2xl font-body font-light max-w-3xl mx-auto mb-8 leading-relaxed">
                Enter the Grid. Experience the future of technology, innovation,
                and human potential at the most electrifying symposium of the
                digital age.
              </p>

              {/* Decorative Line */}
              <div className="neon-line-red max-w-md mx-auto mb-12" />

              {/* CTA Buttons - Highlighted "Explore Program" */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  className="group relative px-8 py-4 bg-primary rounded-lg font-bold text-white text-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(var(--color-primary-rgb),0.8)] animate-pulse-glow"
                  onClick={() => handleNavigate("/events")}
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <Sparkles className="w-6 h-6" />
                    <span>Explore Program</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent to-primary/90 animate-gradient-shift" />
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 px-4 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-card/50 to-transparent" />
          <div className="container max-w-6xl mx-auto relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`text-center transition-all duration-700 ${
                    isLoaded
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-10"
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div
                    className={`neon-card p-6 ${
                      index % 2 === 0 ? "" : "neon-card-red border-secondary/50"
                    }`}
                  >
                    <div
                      className={`font-display text-4xl md:text-5xl font-black mb-2 ${
                        index % 2 === 0
                          ? "text-primary text-glow-blue"
                          : "text-secondary text-glow-red"
                      }`}
                    >
                      {stat.value}
                    </div>
                    <div className="text-foreground/60 font-body text-lg uppercase tracking-widest">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section 1 - Logo Left */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left - Logo Placeholder */}
              <div
                className={`relative transition-all duration-700 delay-200 ${
                  isLoaded
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-10"
                }`}
              >
                <div className="relative aspect-square max-w-xs mx-auto">
                  {/* Outer Ring */}
                  <div className="absolute inset-0 neon-circle animate-glow-pulse" />

                  {/* Inner Ring */}
                  <div className="absolute inset-6 neon-circle-red animate-glow-pulse-red" />

                  {/* Logo Container with Image */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 md:w-56 md:h-56 flex items-center justify-center bg-card/50 backdrop-blur-sm overflow-hidden">
                      {/* Replace with your actual logo image */}
                      <img
                        src="https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/website/ASTRANOVA.png"
                        alt="Mission One Logo"
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          e.currentTarget.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className =
                            "font-display text-primary text-glow-blue text-xs tracking-[0.15em] uppercase text-center px-2";
                          fallback.textContent = "Logo 1";
                          e.currentTarget.parentElement?.appendChild(fallback);
                        }}
                      />
                    </div>
                  </div>

                  {/* Corner Accents */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-primary" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-secondary" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-secondary" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-primary" />
                </div>
              </div>

              {/* Right - Text Content */}
              <div
                className={`transition-all duration-700 delay-400 ${
                  isLoaded
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-10"
                }`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-2 bg-primary glow-blue" />
                  <span className="text-primary font-display text-sm tracking-[0.3em] uppercase">
                    About Astranova
                  </span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-6 text-foreground">
                  REDEFINING THE{" "}
                  <span className="text-primary text-glow-blue">DIGITAL</span>{" "}
                  FRONTIER
                </h2>
                <div className="space-y-4 text-foreground/70 font-body text-lg leading-relaxed">
                  <p>
                    The Department of Information Technology at Coimbatore
                    Institute of Technology (CIT), Coimbatore proudly presents
                    Astranova, a high-energy national-level tech fest that
                    brings innovation and creativity to the forefront. This
                    2-day technological extravaganza unites students,
                    developers, and tech enthusiasts from across the country for
                    an immersive experience of learning and competition.
                    Astranova features 25+ diverse events, including a flagship
                    national-level hackathon, hands-on workshops, expert-led
                    seminars, and competitive technical challenges spanning
                    emerging domains such as software engineering, artificial
                    intelligence, cybersecurity, and future-ready technologies.
                    With over 3000 participants, the event serves as a dynamic
                    platform for problem-solving, collaboration, and idea
                    exchange. More than just a technical fest, Astranova
                    celebrates curiosity, teamwork, and innovation. By blending
                    intense technical engagement with interactive sessions and
                    collaborative learning, the event fosters an inspiring
                    environment where ideas transform into impact and technology
                    meets imagination.
                  </p>
                </div>
                <div className="neon-line mt-8 max-w-xs" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section 2 - Logo Right */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left - Text Content */}
              <div
                className={`transition-all duration-700 delay-200 order-2 md:order-1 ${
                  isLoaded
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-10"
                }`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-2 bg-secondary glow-red" />
                  <span className="text-secondary font-display text-sm tracking-[0.3em] uppercase">
                    About the Department
                  </span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-bold mb-6 text-foreground">
                  DEPARTMENT{" "}
                  <span className="text-secondary text-glow-red">OF</span>{" "}
                  INFORMATION TECHNOLOGY
                </h2>
                <div className="space-y-4 text-foreground/70 font-body text-lg leading-relaxed">
                  <p>
                    The Department of Information of Technology was started in
                    2001 with a vision to provide a place for Innovation,
                    Scientific Discovery and New Technology to evolve as a
                    Centre of Excellence for research and learning, integrating
                    Computer and Information Sciences with Natural Sciences and
                    Basic Engineering. The department prepares over 72 graduates
                    (UG) every year to become leaders in diverse fields such as
                    academia, industry and government.
                  </p>
                </div>
                <div className="neon-line-red mt-8 max-w-xs" />
              </div>

              {/* Right - Logo Placeholder */}
              <div
                className={`relative transition-all duration-700 delay-400 order-1 md:order-2 ${
                  isLoaded
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-10"
                }`}
              >
                <div className="relative aspect-square max-w-xs mx-auto">
                  {/* Outer Ring */}
                  <div className="absolute inset-0 neon-circle-red animate-glow-pulse-red" />

                  {/* Inner Ring */}
                  <div className="absolute inset-6 neon-circle animate-glow-pulse" />

                  {/* Logo Container with Image */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-60 h-60 md:w-72 md:h-72 flex items-center justify-center bg-card/50 backdrop-blur-sm overflow-hidden">
                      {/* Replace with your actual logo image */}
                      <img
                        src="https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/website/ITDEPT.jpg"
                        alt="Mission One Logo"
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          // Fallback if image doesn't exist
                          e.currentTarget.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className =
                            "font-display text-primary text-glow-blue text-xs tracking-[0.15em] uppercase text-center px-2";
                          fallback.textContent = "Logo 1";
                          e.currentTarget.parentElement?.appendChild(fallback);
                        }}
                      />
                    </div>
                  </div>

                  {/* Corner Accents */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-l-2 border-t-2 border-secondary" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-primary" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-primary" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-r-2 border-b-2 border-secondary" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="neon-line-red w-16" />
                <div className="w-2 h-2 bg-secondary glow-red rotate-45" />
                <div className="neon-line-red w-16" />
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
                WHAT{" "}
                <span className="text-secondary text-glow-red">AWAITS</span> YOU
              </h2>
              <p className="text-foreground/60 font-body text-lg max-w-2xl mx-auto">
                Immerse yourself in an experience designed to challenge your
                perceptions and ignite your imagination.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`group relative transition-all duration-700 ${
                    isLoaded
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-10"
                  }`}
                  style={{ transitionDelay: `${index * 150 + 300}ms` }}
                >
                  <div
                    className={`${
                      feature.color === "blue"
                        ? "neon-card hover:border-primary"
                        : "neon-card-red hover:border-secondary"
                    } p-8 h-full transition-all duration-300`}
                  >
                    {/* Icon */}
                    <div
                      className={`text-4xl mb-4 font-display ${
                        feature.color === "blue"
                          ? "text-primary text-glow-blue"
                          : "text-secondary text-glow-red"
                      }`}
                    >
                      {feature.icon}
                    </div>

                    {/* Content */}
                    <h3 className="font-display text-xl font-bold text-foreground mb-3 uppercase tracking-wider">
                      {feature.title}
                    </h3>
                    <p className="text-foreground/60 font-body leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Corner Accent */}
                    <div
                      className={`absolute bottom-0 right-0 w-12 h-12 ${
                        feature.color === "blue"
                          ? "border-r border-b border-primary/50"
                          : "border-r border-b border-secondary/50"
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <div className="neon-card p-12 relative overflow-hidden">
              {/* Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />

              <div className="relative z-10">
                <h2 className="font-display text-3xl md:text-5xl font-black mb-6">
                  <span className="text-foreground">READY TO</span>
                  <br />
                  <span className="text-primary text-glow-blue">
                    ENTER THE
                  </span>{" "}
                  <span className="text-secondary text-glow-red">GRID?</span>
                </h2>
                <p className="text-foreground/70 font-body text-lg mb-8 max-w-xl mx-auto">
                  Secure your place in the most anticipated tech symposium of
                  the year. Limited seats available.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {/* Highlighted "View Schedule" Button */}
                  <button
                    className="group relative px-8 py-4 bg-primary rounded-lg font-bold text-white text-lg overflow-hidden transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(var(--color-primary-rgb),0.8)] animate-pulse-glow"
                    onClick={() => handleNavigate("/clock")}
                  >
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      <Calendar className="w-6 h-6" />
                      <span>View Schedule</span>
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent to-primary/90 animate-gradient-shift" />
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </button>
                </div>
              </div>

              {/* Corner Decorations */}
              <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-primary/50" />
              <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-secondary/50" />
              <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2 border-secondary/50" />
              <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-primary/50" />
            </div>
          </div>
        </section>
      </div>

      {/* Add CSS animations for the highlighted buttons */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(var(--color-primary-rgb), 0.5); }
          50% { box-shadow: 0 0 40px rgba(var(--color-primary-rgb), 0.8), 0 0 60px rgba(var(--color-primary-rgb), 0.4); }
        }
        
        @keyframes pulse-glow-red {
          0%, 100% { box-shadow: 0 0 20px rgba(var(--color-secondary-rgb), 0.5); }
          50% { box-shadow: 0 0 40px rgba(var(--color-secondary-rgb), 0.8), 0 0 60px rgba(var(--color-secondary-rgb), 0.4); }
        }
        
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-pulse-glow-red {
          animation: pulse-glow-red 2s ease-in-out infinite;
        }
        
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default About;
